import AddProductPage from './pages/AddProductPage';
import AutomotiveAddProductPage from './pages/AutomotiveAddProductPage';
import AutomotiveManualPage from './pages/AutomotiveManualPage';
import AutomotivePresetPage from './pages/AutomotivePresetPage';
import AutomotiveInventoryPage from './pages/AutomotiveInventoryPage';
import AutomotiveSuppliersPage from './pages/AutomotiveSuppliersPage';
import AutomotivePurchaseOrderForm from './pages/AutomotivePurchaseOrderForm';
import ProductListPage from './pages/ProductListPage';
import LowStockAlertsPage from './pages/LowStockAlertsPage';
import PharmacyExpiryPage from './pages/PharmacyExpiryPage';
import React, { useState, useLayoutEffect, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import './styles/pageShell.css';
import './styles/factoryTheme.css';
import './styles/appThemeDark.css';

import Sidebar from './Sidebar';
import TopBar from './TopBar';

import SplashScreen from './SplashScreen';
import LoginPage from './LoginPage';
import RegisterPage from './RegisterPage';
import LegalDocumentPage from './pages/LegalDocumentPage';
import DashboardPage from './pages/DashboardPage';
import BusinessReportPage from './pages/BusinessReportPage';
import SalesReportPage from './pages/SalesReportPage';
import InventoryReportPage from './pages/InventoryReportPage';
import StockPage from './pages/StockPage';
import SalesPage from './pages/SalesPage';
import PrintingPage from './pages/PrintingPage';
import CashPage from './pages/CashPage';
import SmsInboundMonitorPage from './pages/SmsInboundMonitorPage';
import ReportsPage from './pages/ReportsPage';
import LoanPackPage from './pages/LoanPackPage';
import ReportDailyPage from './pages/ReportDailyPage';
import BusinessFinancialReportPage from './pages/BusinessFinancialReportPage';
import ReportWeeklyPage from './pages/ReportWeeklyPage';
import ReportMonthlyPage from './pages/ReportMonthlyPage';
import ReportPrintingPage from './pages/ReportPrintingPage';
import ReportStockPage from './pages/ReportStockPage';
import ReportWakalaPage from './pages/ReportWakalaPage';
import UsersListPage from './pages/UsersListPage';
import UsersAddPage from './pages/UsersAddPage';
import SaleProductsPage from './pages/SaleProductsPage';
import SaleHistoryPage from './pages/SaleHistoryPage';
import OtherServicesSalesPage from './pages/OtherServicesSalesPage';
import ExpensesAdd from './pages/ExpensesAdd';
import BossExpenses from './pages/expenses/BossExpenses';
import EmployeeExpenses from './pages/expenses/EmployeeExpenses';
import OfficeExpenses from './pages/expenses/OfficeExpenses';
import ExpensesSummary from './pages/expenses/ExpensesSummary';
import { hasApiAccessToken } from './apiClient';
import { useAuth } from './AuthContext';
import { normalizeRole } from './utils/userLocale';
import { employeeMayAccessPage, isStaffEmployee, isStaffSettingsPage } from './utils/employeeAccess';
import {
  getEnabledModules,
  isSettingsPage,
  subscriptionAllowsAccess,
  tenantMayAccessPage,
} from './utils/tenantModules';
import AccessRestrictedPage from './pages/AccessRestrictedPage';
import './pages/AccessRestrictedPage.css';
import SecurityActivityLogPage from './pages/SecurityActivityLogPage';
import SecurityChangePasswordPage from './pages/SecurityChangePasswordPage';
import SecurityTipsPage from './pages/SecurityTipsPage';
import SettingsHubPage from './pages/SettingsHubPage';
import SettingsNotificationsPage from './pages/SettingsNotificationsPage';
import LanguageToggle from './components/LanguageToggle';
import ThemeToggle from './components/ThemeToggle';
import { useLanguage } from './i18n/LanguageContext';
import { getLandingFaq, getLandingFeatureCards, getPublicNavLinks } from './i18n/publicContent';
import { applyTheme, initThemeListener, readStoredTheme } from './utils/appTheme';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionCheckoutPage from './pages/SubscriptionCheckoutPage';
import { fileToProfilePhotoDataUrl } from './utils/profilePhotoUpload';
import OrdersPage from './pages/OrdersPage';
import FactorySuppliersPage from './pages/FactorySuppliersPage';
import AddRawMaterialPage from './pages/AddRawMaterialPage';
import AddRawMaterialCategoryPage from './pages/AddRawMaterialCategoryPage';
import BrickFactoryPage from './pages/BrickFactoryPage';
import FactoryBrickDamagePage from './pages/FactoryBrickDamagePage';
import FactoryBrickPreOrdersPage from './pages/FactoryBrickPreOrdersPage';
import FactoryDashboardPage from './pages/FactoryDashboardPage';
import BrickCustomerDocsPage from './pages/BrickCustomerDocsPage';
import ShopCustomerDocsPage from './pages/ShopCustomerDocsPage';
import { isBrickFactoryUser, tenantDisplayName, isAutomotiveBusiness } from './utils/businessType';
import BranchesPage from './pages/BranchesPage';
import BusinessSettingsPage from './pages/BusinessSettingsPage';
import CustomerDebtsPage from './pages/CustomerDebtsPage';
import RawMaterialsOverview from './pages/RawMaterialsOverview';
import FactoryStockOverviewPage from './pages/FactoryStockOverviewPage';
import FactoryBrickTypesSetupPage from './pages/FactoryBrickTypesSetupPage';
import WhatsAppFloatingButton from './components/WhatsAppFloatingButton';
import { FALLBACK_BUSINESS_TYPES } from './constants/businessTypes';
import { useLandingReveal } from './hooks/useLandingReveal';

/** Landing pills: same labels as signup (`/public/business-types` fallback). */
const LANDING_SIGNUP_BUSINESS_TYPES = FALLBACK_BUSINESS_TYPES.map((t) => ({
  key: t.code,
  label: t.label,
}));

const SPLASH_MIN_MS = 900;
const FLASH_MS = 2400;

function App() {
  const { user, authLoading, login, logout, updateProfile } = useAuth();
  const { t, lang } = useLanguage();
  const [page, setPage] = useState(null);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [splashMinDone, setSplashMinDone] = useState(false);
  const [flash, setFlash] = useState(null);
  const [publicModule, setPublicModule] = useState('home');
  const [legalReturnModule, setLegalReturnModule] = useState('register');
  const [activeNavSection, setActiveNavSection] = useState('home');
  const pendingScrollRef = useRef(null);

  const landingRevealActive = !user && publicModule === 'home';
  useLandingReveal(landingRevealActive);

  useEffect(() => {
    const factory = user && isBrickFactoryUser(user);
    document.body.classList.toggle('app-theme-factory', !!factory);
    return () => document.body.classList.remove('app-theme-factory');
  }, [user]);

  useEffect(() => {
    if (!user?.id) {
      applyTheme(readStoredTheme('guest'), 'guest');
      return initThemeListener('guest');
    }
    const theme = user.preferences?.theme || readStoredTheme(user.id);
    applyTheme(theme, user.id);
    return initThemeListener(user.id);
  }, [user?.id, user?.preferences?.theme]);

  const scrollToLandingSection = useCallback(
    (sectionKey) => {
      const performScroll = () => {
        if (sectionKey === 'home') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setActiveNavSection('home');
          return;
        }
        const el = document.getElementById(sectionKey);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setActiveNavSection(sectionKey);
        }
      };

      if (
        publicModule === 'login' ||
        publicModule === 'register' ||
        publicModule === 'terms' ||
        publicModule === 'privacy'
      ) {
        pendingScrollRef.current = sectionKey;
        setPublicModule('home');
        return;
      }
      performScroll();
    },
    [publicModule]
  );

  useEffect(() => {
    if (user) return undefined;
    if (publicModule !== 'home' || !pendingScrollRef.current) return undefined;
    const target = pendingScrollRef.current;
    pendingScrollRef.current = null;
    const t = window.setTimeout(() => {
      if (target === 'home') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setActiveNavSection('home');
      } else {
        const el = document.getElementById(target);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setActiveNavSection(target);
        }
      }
    }, 80);
    return () => window.clearTimeout(t);
  }, [user, publicModule]);

  useEffect(() => {
    if (user) return undefined;
    const onScroll = () => {
      if (window.scrollY < 100) setActiveNavSection('home');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [user]);

  useEffect(() => {
    if (user || publicModule !== 'home') return undefined;
    const ids = ['features', 'industries', 'faq', 'support'];
    const sections = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0 && window.scrollY >= 100) {
          setActiveNavSection(visible[0].target.id);
        }
      },
      { rootMargin: '-20% 0px -55% 0px', threshold: [0.15, 0.4] }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [user, publicModule]);

  const resolveInitialBusinessPage = (userData) => {
    // After sign-in / register, always open the dashboard overview (not orders or other modules).
    if (tenantMayAccessPage('overview', userData)) {
      return 'overview';
    }

    const enabledModules = getEnabledModules(userData);
    const has = (moduleKey) => !enabledModules || enabledModules.includes(moduleKey);

    const fallbackByModule = [
      ['stock', 'stock-list'],
      ['pos', 'pos-sale-products'],
      ['orders', 'orders-make'],
      ['printing', 'printing'],
      ['expenses', 'expenses-summary'],
      ['cashbook', 'cash'],
      ['reports', 'report-daily'],
      ['users', 'users-list'],
      ['security', 'settings'],
    ];

    for (const [mod, pageKey] of fallbackByModule) {
      if (
        has(mod) &&
        tenantMayAccessPage(pageKey, userData) &&
        employeeMayAccessPage(pageKey, userData.role)
      ) {
        return pageKey;
      }
    }

    return 'overview';
  };

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const apply = () => {
      if (mq.matches) setSidebarOpen(false);
      else setSidebarOpen(true);
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setSplashMinDone(true), SPLASH_MIN_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!flash) return undefined;
    const timer = window.setTimeout(() => setFlash(null), FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [flash]);

  useLayoutEffect(() => {
    if (user && !page) {
      setPage(resolveInitialBusinessPage(user));
    }
  }, [user, page]);

  useEffect(() => {
    if (!user || !page) return;
    if (isStaffEmployee(user.role) && isStaffSettingsPage(page)) {
      setPage('overview');
    }
  }, [user, page]);

  const handleLogin = (userData) => {
    login(userData);
    setPublicModule('login');
    setPage(resolveInitialBusinessPage(userData));
    setFlash({ type: 'success', text: 'Login successful. Redirecting...' });
  };

  const handleRegister = (userData) => {
    login(userData);
    setPublicModule('login');
    setPage(resolveInitialBusinessPage(userData));
    const biz = userData?.tenant?.name || 'your company';
    const typeLabel = userData?.tenant?.businessTypeLabel || '';
    setFlash({
      type: 'success',
      text: `Welcome to ${biz}! Your workspace is set up for ${typeLabel || 'your business type'} — menus show only your modules.`,
    });
  };

  const handleProfileUpload = async (file) => {
    try {
      const dataUrl = await fileToProfilePhotoDataUrl(file);
      await updateProfile(dataUrl);
    } catch {
      /* optional: surface toast */
    }
  };

  const handleLogout = async () => {
    setLogoutLoading(true);
    setPage(null);
    setPublicModule('home');
    try {
      await logout();
      setFlash({ type: 'success', text: 'Logout successful.' });
    } finally {
      setLogoutLoading(false);
    }
  };

  const flashNode = flash ? (
    <div className={`app-flash app-flash--${flash.type || 'success'}`} role="status">
      {flash.text}
    </div>
  ) : null;

  const renderPage = () => {
    if (!user) return null;
    const pageKey = page || 'overview';
    const billingPage = pageKey === 'subscription' || pageKey === 'subscription-checkout';
    const settingsAccessible = isSettingsPage(pageKey);
    if (!subscriptionAllowsAccess(user) && !billingPage && !settingsAccessible) {
      return (
        <div className="page-bleed">
          <div className="access-restricted-root">
            <h1>Subscription expired</h1>
            <p>
              Your trial or payment period has ended. Choose a plan and continue to payment to restore access to the
              system.
            </p>
            <div className="access-restricted-actions">
              <button type="button" className="access-restricted-btn" onClick={() => setPage('subscription')}>
                Go to plans &amp; billing
              </button>
              <button type="button" className="access-restricted-btn access-restricted-btn--ghost" onClick={handleLogout}>
                Sign out
              </button>
            </div>
          </div>
        </div>
      );
    }
    if (!tenantMayAccessPage(pageKey, user)) {
      return <AccessRestrictedPage onNavigate={setPage} pageKey={pageKey} user={user} reason="module" />;
    }
    if (!employeeMayAccessPage(pageKey, user.role)) {
      if (isStaffEmployee(user.role) && isStaffSettingsPage(pageKey)) {
        return <DashboardPage user={user} onNavigate={setPage} />;
      }
      return <AccessRestrictedPage onNavigate={setPage} pageKey={pageKey} user={user} reason="role" />;
    }
    switch (pageKey) {
            case 'damaged-products':
              return isBrickFactoryUser(user) ? (
                <FactoryBrickDamagePage onNavigate={setPage} />
              ) : (
                React.createElement(require('./pages/DamagedProductsPage').default, { onNavigate: setPage })
              );
      case 'product-request':
        return React.createElement(require('./pages/ProductRequestPage').default, { onNavigate: setPage });
      case 'overview':
      case 'owner-dashboard':
      case 'employee-dashboard':
      case 'dashboard':
        return <DashboardPage user={user} onNavigate={setPage} />;
      case 'branches-list':
        return <BranchesPage onNavigate={setPage} user={user} />;
      case 'business-settings':
        return <BusinessSettingsPage onNavigate={setPage} />;
      case 'users-list':
        return <UsersListPage onNavigate={setPage} user={user} />;
      case 'users-add':
        return <UsersAddPage onNavigate={setPage} user={user} />;
      case 'pos-sale-products':
        return <SaleProductsPage onNavigate={setPage} />;
      case 'pos-sale-history':
        return <SaleHistoryPage onNavigate={setPage} />;
      case 'pos-other-services':
        return <OtherServicesSalesPage onNavigate={setPage} />;
      case 'debts-list':
        return <CustomerDebtsPage onNavigate={setPage} />;
      case 'stock':
        return isBrickFactoryUser(user) ? (
          <RawMaterialsOverview onNavigate={setPage} />
        ) : (
          <StockPage onNavigate={setPage} />
        );
      case 'add-raw-material':
        return <AddRawMaterialPage onNavigate={setPage} />;
      case 'add-raw-material-category':
        return <AddRawMaterialCategoryPage onNavigate={setPage} />;
      case 'stock-add':
        if (isAutomotiveBusiness(user)) {
          return <AutomotiveAddProductPage onNavigate={setPage} />;
        }
        return <AddProductPage onNavigate={setPage} />;
      case 'auto-products-record':
        return <AutomotiveAddProductPage onNavigate={setPage} initialSection="record" />;
      case 'auto-products-opening':
      case 'auto-opening-stock':
        return <AutomotiveAddProductPage onNavigate={setPage} initialSection="opening-stock" />;
      case 'auto-inventory':
        return <AutomotiveInventoryPage onNavigate={setPage} />;
      case 'auto-suppliers':
        return <AutomotiveSuppliersPage onNavigate={setPage} />;
      case 'auto-manual':
        return <AutomotiveManualPage onNavigate={setPage} />;
      case 'auto-products-categories':
        return <AutomotivePresetPage key="auto-categories" type="categories" onNavigate={setPage} />;
      case 'auto-products-brands':
        return <AutomotivePresetPage key="auto-brands" type="brands" onNavigate={setPage} />;
      case 'auto-products-units':
        return <AutomotivePresetPage key="auto-units" type="units" onNavigate={setPage} />;
      case 'stock-list':
        return <ProductListPage />;
      case 'stock-pending':
        return <ProductListPage pendingOnly />;
      case 'stock-alerts':
        return <LowStockAlertsPage onNavigate={setPage} />;
      case 'pharmacy-expiry':
        return <PharmacyExpiryPage onNavigate={setPage} />;
      case 'sales':
        return <SalesPage onNavigate={setPage} />;
      case 'printing':
        return <PrintingPage mode="hub" onNavigate={setPage} />;
      case 'printing-new-job':
        return <PrintingPage mode="new-job" onNavigate={setPage} />;
      case 'printing-payments':
        return <PrintingPage mode="payments" onNavigate={setPage} />;
      case 'printing-history':
        return <PrintingPage mode="history" onNavigate={setPage} />;
      case 'printing-rates':
        return <PrintingPage mode="rates" onNavigate={setPage} />;
      case 'cash':
        return <CashPage onNavigate={setPage} />;
      case 'cash-new':
        return <CashPage mode="new" onNavigate={setPage} />;
      case 'cash-history':
        return <CashPage mode="history" onNavigate={setPage} />;
      case 'cash-insights':
        return <CashPage mode="insights" onNavigate={setPage} />;
      case 'cash-sms-monitor':
        return <SmsInboundMonitorPage onNavigate={setPage} />;
      case 'reports':
        return <ReportsPage user={user} onNavigate={setPage} />;
      case 'report-financial':
        return <BusinessFinancialReportPage user={user} onNavigate={setPage} initialPeriod="custom" />;
      case 'report-daily':
        return <ReportDailyPage user={user} onNavigate={setPage} />;
      case 'report-weekly':
        return <ReportWeeklyPage user={user} onNavigate={setPage} />;
      case 'report-monthly':
        return <ReportMonthlyPage user={user} onNavigate={setPage} />;
      case 'report-printing':
        return <ReportPrintingPage onNavigate={setPage} />;
      case 'report-stock':
        return <ReportStockPage onNavigate={setPage} />;
      case 'report-wakala':
        return <ReportWakalaPage onNavigate={setPage} />;
      case 'report-loan-pack':
        return <LoanPackPage user={user} onNavigate={setPage} />;
      case 'settings':
        return <SettingsHubPage onNavigate={setPage} />;
      case 'settings-modules':
        return <SettingsHubPage onNavigate={setPage} />;
      case 'settings-notifications':
        return <SettingsNotificationsPage onNavigate={setPage} />;
      case 'settings-appearance':
        return <SettingsHubPage onNavigate={setPage} />;
      case 'settings-account':
        return <SettingsHubPage onNavigate={setPage} />;
      case 'subscription':
        return <SubscriptionPage user={user} onNavigate={setPage} />;
      case 'subscription-checkout':
        return <SubscriptionCheckoutPage user={user} onNavigate={setPage} />;
      case 'security-logs':
        return <SecurityActivityLogPage onNavigate={setPage} />;
      case 'security-password':
        return <SecurityChangePasswordPage onNavigate={setPage} />;
      case 'support':
      case 'security-tips':
        return <SecurityTipsPage onNavigate={setPage} />;
      case 'expenses-add':
        return <ExpensesAdd user={user} onNavigate={setPage} />;
      case 'expenses-boss-history':
        return <BossExpenses user={user} onAddExpense={() => setPage('expenses-add')} />;
      case 'expenses-employee-history':
        return <EmployeeExpenses user={user} onAddExpense={() => setPage('expenses-add')} />;
      case 'expenses-office-history':
        return <OfficeExpenses user={user} onAddExpense={() => setPage('expenses-add')} />;
      case 'expenses-summary':
        return <ExpensesSummary user={user} onNavigate={setPage} />;
      case 'factory-suppliers':
        return <FactorySuppliersPage onNavigate={setPage} />;
      case 'orders-make':
        if (isAutomotiveBusiness(user)) {
          return (
            <div className="orders-page page-bleed">
              <header className="orders-page-header">
                <h1 className="orders-page-title">New purchase order</h1>
                <p className="orders-page-sub">
                  Chagua supplier na spare parts kutoka kwenye catalog. Weka bei ya kununua hapa; bei ya
                  kuuza utaweka ukishapokea mzigo.
                </p>
              </header>
              <div className="orders-make-panel">
                <AutomotivePurchaseOrderForm onNavigate={setPage} />
              </div>
            </div>
          );
        }
        return (
          <OrdersPage
            initialTab="make"
            onNavigate={setPage}
            factorySection={isBrickFactoryUser(user) ? 'order' : undefined}
          />
        );
      case 'orders-tracking':
        return (
          <OrdersPage
            initialTab="list"
            onNavigate={setPage}
            factorySection={
              isBrickFactoryUser(user) || isAutomotiveBusiness(user) ? 'tracking' : undefined
            }
          />
        );
      case 'orders-list':
        if (isAutomotiveBusiness(user)) {
          return (
            <OrdersPage initialTab="list" onNavigate={setPage} factorySection="tracking" />
          );
        }
        return (
          <OrdersPage
            initialTab="list"
            onNavigate={setPage}
            factorySection={isBrickFactoryUser(user) ? 'tracking' : undefined}
          />
        );
      case 'orders':
        return <OrdersPage initialTab="make" onNavigate={setPage} />;
      case 'brick':
        if (isBrickFactoryUser(user)) {
          return <FactoryDashboardPage user={user} onNavigate={setPage} />;
        }
        return <BrickFactoryPage mode="hub" onNavigate={setPage} />;
      case 'brick-catalog':
        return <BrickFactoryPage mode="catalog" onNavigate={setPage} />;
      case 'brick-production':
        return <BrickFactoryPage mode="production" onNavigate={setPage} />;
      case 'brick-deliveries':
        return <BrickFactoryPage mode="deliveries" onNavigate={setPage} />;
      case 'brick-pre-orders':
        return <FactoryBrickPreOrdersPage onNavigate={setPage} />;
      case 'brick-customer-docs':
        return <BrickCustomerDocsPage onNavigate={setPage} />;
      case 'shop-customer-docs':
        return <ShopCustomerDocsPage onNavigate={setPage} />;
      case 'raw-materials-overview':
        return <RawMaterialsOverview onNavigate={setPage} />;
      case 'factory-stock-overview':
        return <FactoryStockOverviewPage onNavigate={setPage} />;
      case 'factory-brick-types':
        return <FactoryBrickTypesSetupPage onNavigate={setPage} />;
      default:
        return <DashboardPage user={user} onNavigate={setPage} />;
    }
  };

  const mayHaveSession = hasApiAccessToken();
  const blockOnAuth = authLoading && mayHaveSession;

  if (logoutLoading || !splashMinDone || blockOnAuth) {
    return <SplashScreen />;
  }
  if (!user) {
    const navLinks = getPublicNavLinks(t);
    const landingFaq = getLandingFaq(t);

    const goRegister = () => setPublicModule('register');
    const goLogin = () => setPublicModule('login');
    const goHome = () => setPublicModule('home');
    const goTerms = (from = 'register') => {
      setLegalReturnModule(from);
      setPublicModule('terms');
      window.scrollTo(0, 0);
    };
    const goPrivacy = (from = 'register') => {
      setLegalReturnModule(from);
      setPublicModule('privacy');
      window.scrollTo(0, 0);
    };
    const goBackFromLegal = () => setPublicModule(legalReturnModule || 'register');

    if (publicModule === 'terms' || publicModule === 'privacy') {
      const backLabels = {
        register: t('public.legal.backRegister'),
        login: t('public.legal.backLogin'),
        home: t('public.legal.backHome'),
      };
      return (
        <>
          {flashNode}
          <LegalDocumentPage
            type={publicModule}
            backLabel={backLabels[legalReturnModule] || 'Go back'}
            onBack={goBackFromLegal}
            onGoHome={goHome}
            onLogin={goLogin}
            onRegister={goRegister}
            onShowTerms={() => goTerms(legalReturnModule)}
            onShowPrivacy={() => goPrivacy(legalReturnModule)}
          />
        </>
      );
    }

    if (publicModule === 'login') {
      return (
        <>
          {flashNode}
          <LoginPage
            onLogin={handleLogin}
            onShowRegister={goRegister}
            onGoHome={goHome}
          />
        </>
      );
    }

    if (publicModule === 'register') {
      return (
        <>
          {flashNode}
          <RegisterPage
            onRegister={handleRegister}
            onShowLogin={goLogin}
            onShowRegister={goRegister}
            onGoHome={goHome}
            onShowTerms={() => goTerms('register')}
            onShowPrivacy={() => goPrivacy('register')}
          />
        </>
      );
    }

    const renderLandingContent = () => {
      const landingFeatureCards = getLandingFeatureCards(t);

      return (
        <main className="lp-main lp-main--animate" aria-live="polite">
          {/* ── HERO ── */}
          <section className="lp-hero" id="home">
            <div className="lp-hero__inner">
              <h1 className="lp-hero__h1">
                {t('public.hero.title')}
                <br />
                <span className="lp-hero__accent lp-hero__accent--shine">{t('public.hero.accent')}</span>
              </h1>
              <p className="lp-hero__lead">{t('public.hero.lead')}</p>
              <div className="lp-hero__actions">
                <button type="button" className="lp-btn lp-btn--primary" onClick={goRegister}>
                  {t('public.nav.register')}
                </button>
                <button type="button" className="lp-btn lp-btn--ghost" onClick={goLogin}>
                  {t('public.nav.signIn')}
                </button>
              </div>
              <div className="lp-hero__stats">
                <div className="lp-stat lp-hero__stat-item" style={{ animationDelay: '0.34s' }}>
                  <strong>500+</strong>
                  <span>{t('public.hero.statBusinesses')}</span>
                </div>
                <div className="lp-stat__divider lp-hero__stat-item" style={{ animationDelay: '0.4s' }} aria-hidden />
                <div className="lp-stat lp-hero__stat-item" style={{ animationDelay: '0.46s' }}>
                  <strong>{LANDING_SIGNUP_BUSINESS_TYPES.length}</strong>
                  <span>{t('public.hero.statTypes')}</span>
                </div>
                <div className="lp-stat__divider lp-hero__stat-item" style={{ animationDelay: '0.52s' }} aria-hidden />
                <div className="lp-stat lp-hero__stat-item" style={{ animationDelay: '0.58s' }}>
                  <strong>24/7</strong>
                  <span>{t('public.hero.statSupport')}</span>
                </div>
              </div>
            </div>
            <div className="lp-hero__visual" aria-hidden>
              <div className="lp-hero__glow" />
              <div className="lp-mockup">
                <div className="lp-mockup__bar">
                  <span /><span /><span />
                </div>
                <div className="lp-mockup__body">
                  <div className="lp-mockup__row lp-mockup__row--head" />
                  <div className="lp-mockup__row" />
                  <div className="lp-mockup__row lp-mockup__row--wide" />
                  <div className="lp-mockup__row" />
                  <div className="lp-mockup__row lp-mockup__row--sm" />
                  <div className="lp-mockup__grid">
                    <div className="lp-mockup__card lp-mockup__card--g" />
                    <div className="lp-mockup__card lp-mockup__card--t" />
                    <div className="lp-mockup__card lp-mockup__card--a" />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── FEATURES ── */}
          <section className="lp-section lp-features" id="features">
            <div className="lp-section__inner">
              <p className="lp-section__eyebrow lp-reveal lp-reveal--fade">{t('public.features.eyebrow')}</p>
              <h2 className="lp-section__h2 lp-reveal" data-delay="1">
                {t('public.features.title')}
              </h2>
              <p className="lp-features__intro lp-reveal" data-delay="2">
                {t('public.features.intro')}
              </p>
              <div className="lp-feat-grid">
                {landingFeatureCards.map((f, i) => (
                  <div
                    className="lp-feat-card lp-reveal lp-reveal--scale"
                    key={f.id}
                    style={{ transitionDelay: `${0.05 + (i % 6) * 0.07}s` }}
                  >
                    <div className="lp-feat-card__icon">{f.icon}</div>
                    <h3>{f.title}</h3>
                    <p>{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── BUSINESS TYPES ── */}
          <section className="lp-section lp-industries" id="industries" aria-labelledby="lp-industries-title">
            <div className="lp-section__inner lp-industries__inner">
              <p className="lp-industries__eyebrow lp-reveal lp-reveal--fade">{t('public.industries.eyebrow')}</p>
              <h2 id="lp-industries-title" className="lp-industries__h2 lp-reveal" data-delay="1">
                {t('public.industries.title')}
              </h2>
              <p className="lp-industries__lead lp-reveal" data-delay="2">
                {t('public.industries.lead')}
              </p>
              <ul className="lp-industries__tags" role="list">
                {LANDING_SIGNUP_BUSINESS_TYPES.map((b, i) => (
                  <li
                    key={b.key}
                    className="lp-reveal lp-reveal--scale"
                    style={{ transitionDelay: `${0.04 + (i % 8) * 0.05}s` }}
                  >
                    <span className="lp-industry-pill">{b.label}</span>
                  </li>
                ))}
              </ul>
              <p className="lp-industries__footnote lp-reveal" data-delay="3">
                {t('public.industries.footnote')}
              </p>
            </div>
          </section>

          {/* ── FAQ ── */}
          <section className="lp-section lp-faq" id="faq">
            <div className="lp-section__inner">
              <p className="lp-section__eyebrow lp-reveal lp-reveal--fade">{t('public.faq.eyebrow')}</p>
              <h2 className="lp-section__h2 lp-reveal" data-delay="1">
                {t('public.faq.title')}
              </h2>
              <p className="lp-faq__lead lp-reveal" data-delay="2">
                {t('public.faq.lead')}
              </p>
              <div className="lp-faq__list">
                {landingFaq.map((item, faqIndex) => (
                  <details
                    key={item.q}
                    className="lp-faq__item lp-reveal"
                    style={{ transitionDelay: `${0.06 + faqIndex * 0.07}s` }}
                  >
                    <summary className="lp-faq__question">{item.q}</summary>
                    <p className="lp-faq__answer">{item.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* ── SUPPORT / CTA ── */}
          <section className="lp-section lp-cta-band" id="support">
            <div className="lp-section__inner lp-cta-band__inner">
              <div className="lp-cta-band__copy">
                <h2 className="lp-cta-band__h2 lp-reveal">{t('public.cta.title')}</h2>
                <p className="lp-cta-band__sub lp-reveal" data-delay="1">
                  {t('public.cta.sub')}
                </p>
                <div className="lp-cta-band__contacts lp-reveal" data-delay="2">
                  <span>📞 0711416873</span>
                  <span>✉️ support@cbonline.co.tz</span>
                </div>
              </div>
              <div className="lp-cta-band__btns lp-reveal lp-reveal--right" data-delay="2">
                <button type="button" className="lp-btn lp-btn--primary lp-btn--lg" onClick={goRegister}>
                  {t('public.nav.register')}
                </button>
                <button type="button" className="lp-btn lp-btn--ghost lp-btn--lg" onClick={goLogin}>
                  {t('public.nav.signIn')}
                </button>
              </div>
            </div>
          </section>

          {/* ── FOOTER ── */}
          <footer className="lp-footer">
            <div className="lp-footer__inner">
              <div className="lp-footer__brand">
                <img src="/branding/cb-app-logo-circle.svg" alt="" className="lp-footer__logo" />
                <span>cbonline app</span>
              </div>
              <p className="lp-footer__copy">{t('public.footer.rights')}</p>
            </div>
          </footer>
        </main>
      );
    };

    return (
      <div className="lp-shell" key={lang}>
        {flashNode}
        <header className="lp-nav">
          <div className="lp-nav__inner">
            <button type="button" className="lp-nav__brand" onClick={() => scrollToLandingSection('home')}>
              <img src="/branding/cb-app-logo-circle.svg" alt="cbonline" className="lp-nav__logo" />
              <span className="lp-nav__name">cbonline <strong>app</strong></span>
            </button>
            <nav className="lp-nav__links" aria-label="Main navigation">
              {navLinks.map((l) => (
                <button
                  key={l.key}
                  type="button"
                  className={`lp-nav__link${activeNavSection === l.key ? ' is-active' : ''}`}
                  onClick={() => scrollToLandingSection(l.key)}
                >
                  {l.label}
                </button>
              ))}
            </nav>
            <div className="lp-nav__actions">
              <button type="button" className="lp-nav__login" onClick={goLogin}>
                {t('public.nav.signIn')}
              </button>
              <button type="button" className="lp-nav__cta" onClick={goRegister}>
                {t('public.nav.register')}
              </button>
            </div>
            <div className="lp-nav__toggles">
              <LanguageToggle variant="public" />
              <ThemeToggle variant="public" />
            </div>
            <button
              type="button"
              className="lp-nav__burger"
              aria-label={t('public.nav.menu')}
              onClick={() => {
                const m = document.getElementById('lp-mobile-menu');
                if (m) m.classList.toggle('is-open');
              }}
            >
              ≡
            </button>
          </div>
          <div id="lp-mobile-menu" className="lp-mobile-menu">
            {navLinks.map((l) => (
              <button
                key={l.key}
                type="button"
                className={`lp-mobile-menu__link${activeNavSection === l.key ? ' is-active' : ''}`}
                onClick={() => {
                  scrollToLandingSection(l.key);
                  const m = document.getElementById('lp-mobile-menu');
                  if (m) m.classList.remove('is-open');
                }}
              >
                {l.label}
              </button>
            ))}
            <button type="button" className="lp-btn lp-btn--outline" onClick={goLogin}>
              {t('public.nav.signIn')}
            </button>
            <button type="button" className="lp-btn lp-btn--primary" onClick={goRegister}>
              {t('public.nav.register')}
            </button>
          </div>
        </header>
        <div className="lp-body">{renderLandingContent()}</div>
        <WhatsAppFloatingButton />
      </div>
    );
  }

  const r = normalizeRole(user?.role);
  const factoryTenant = isBrickFactoryUser(user);
  let topBarTitle = 'Dashboard';
  if (factoryTenant) {
    topBarTitle = tenantDisplayName(user) || 'Dashboard';
  } else if (r === 'owner') topBarTitle = tenantDisplayName(user) || 'Owner dashboard';
  else if (r === 'employee') topBarTitle = 'Staff dashboard';
  const ownerOnlyRoute = (element) => (normalizeRole(user?.role) === 'owner' ? element : <AccessRestrictedPage onNavigate={setPage} />);

  return (
    <Router>
      <div className="app-root">
        {flashNode}
        <TopBar
          user={user}
          onProfileUpload={handleProfileUpload}
          realTimeInfo=""
          title={topBarTitle}
          sessionStartedAt={user?.sessionStartedAt}
          onLogout={handleLogout}
          onMenuToggle={() => setSidebarOpen((o) => !o)}
          onNavigate={setPage}
        />
        <div className={`app-body${sidebarOpen ? ' app-body--nav-drawer-open' : ''}`}>
          <Sidebar
            onNavigate={setPage}
            currentUser={user}
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
          />
          <main className="app-main">
            <Routes>
              <Route path="/" element={renderPage()} />
              <Route path="/product-request" element={React.createElement(require('./pages/ProductRequestPage').default, { onNavigate: setPage })} />
              <Route path="/reports/business" element={ownerOnlyRoute(<BusinessReportPage />)} />
              <Route path="/reports/sales" element={ownerOnlyRoute(<SalesReportPage />)} />
              <Route path="/reports/inventory" element={ownerOnlyRoute(<InventoryReportPage />)} />
              <Route path="/stock/add" element={isAutomotiveBusiness(user) ? <AutomotiveAddProductPage onNavigate={setPage} /> : <AddProductPage onNavigate={setPage} />} />
              <Route path="/stock/list" element={<ProductListPage />} />
              <Route path="/stock/alerts" element={<LowStockAlertsPage />} />
              <Route path="/expenses/add" element={<ExpensesAdd user={user} onNavigate={setPage} />} />
              <Route path="/boss-expenses" element={<BossExpenses user={user} onAddExpense={() => setPage('expenses-add')} />} />
              <Route path="/employee-expenses" element={<EmployeeExpenses user={user} onAddExpense={() => setPage('expenses-add')} />} />
              <Route path="/office-expenses" element={<OfficeExpenses user={user} onAddExpense={() => setPage('expenses-add')} />} />
              <Route path="/expenses-summary" element={<ExpensesSummary user={user} onNavigate={setPage} />} />
              <Route path="/orders" element={<OrdersPage initialTab="make" onNavigate={setPage} />} />
              <Route path="/orders/list" element={<OrdersPage initialTab="list" onNavigate={setPage} />} />
            </Routes>
          </main>
        </div>
        <WhatsAppFloatingButton />
      </div>
    </Router>
  );
}

export default App;
